import { useMemo, useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { useDocumentCoverageQuery } from '@/features/document-templates';
import { SearchBar, Pagination, Select, Checkbox, ButtonLink } from '@/shared/components';
import { useProductsQuery } from '@/features/products/hooks/useProductsQuery';
import { ProductDataTable } from '../components/ProductDataTable';

export function ProductList() {
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<'active' | ''>('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [showRetired, setShowRetired] = useState(false);

    const resetPage = () => setPage(1);

    const sortBy = sorting[0]?.id ?? '';
    const sortDir = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : 'asc';

    const query = useProductsQuery({
        search: search.length >= 2 ? search : undefined,
        status: status === 'active' ? 'active' : undefined,
        page,
        per_page: perPage,
        sort_by: sortBy || undefined,
        sort_dir: sortBy ? sortDir : undefined,
        show_retired: showRetired || undefined,
    });

    const products = useMemo(() => query.data?.data ?? [], [query.data]);
    const meta = query.data?.meta;

    const { hasPermission } = usePermissions();
    const canSeeCoverage = hasPermission({ resource: 'document_assignment', action: 'browse' });
    const productIds = useMemo(() => products.map((product) => product.id), [products]);
    const documentCoverage = useDocumentCoverageQuery(productIds, canSeeCoverage);

    const handleSortingChange = (newSorting: SortingState) => {
        setSorting(newSorting);
        resetPage();
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Products</h1>
                <PermissionGuard permission={{ resource: 'smr', action: 'product.add' }}>
                    <ButtonLink to="/smr-admin/products/new" size="sm">New Product</ButtonLink>
                </PermissionGuard>
            </div>

            <div className="flex gap-2 flex-wrap items-center">
                <SearchBar
                    searchTerm={search}
                    setSearchTerm={(term) => { setSearch(term); resetPage(); }}
                    placeholder="Search products"
                />
                <Select
                    id="status-filter"
                    placeholder="Filter by status"
                    value={status}
                    onChange={(v) => { setStatus(v as 'active' | ''); resetPage(); }}
                    options={[
                        { value: '', label: 'All' },
                        { value: 'active', label: 'Active' },
                    ]}
                />
                <Checkbox
                    id="show-retired-filter"
                    label="Show retired only"
                    ariaLabel="Show retired only"
                    checked={showRetired}
                    onChange={(checked) => { setShowRetired(checked); resetPage(); }}
                />
            </div>

            <div className="space-y-4">
                <ProductDataTable
                    data={products}
                    isLoading={query.isLoading}
                    documentCoverage={canSeeCoverage ? documentCoverage : undefined}
                    sorting={sorting}
                    onSortingChange={handleSortingChange}
                />

                {meta && meta.last_page > 1 && (
                    <Pagination
                        currentPage={meta.current_page}
                        lastPage={meta.last_page}
                        total={meta.total}
                        perPage={meta.per_page}
                        from={meta.from}
                        to={meta.to}
                        onPageChange={setPage}
                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                    />
                )}
            </div>
        </section>
    );
}
